import * as THREE from "three/webgpu";
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import type {Subscription} from 'rxjs';
import {RawPointerEventType} from '../viewer/moudles/EventManager';
import type {Viewer} from '../viewer/Viewer';
import { ViewerMode } from '../viewer/ViewerMode';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import type {SketchDrawingHandler, SketchDrawingMouseDownPayload} from '../draw/SketchDrawingHandler';
import {SketchDrawingTool} from '../draw/DrawingSharedTypes';
import {SketchPointCommand, SketchPointDrawingHandler} from '../draw/SketchPointDrawingHandler';
import {SketchLineCommand, SketchLineDrawingHandler} from '../draw/SketchLineDrawingHandler';
import {SketchCircleCommand, SketchCircleDrawingHandler} from '../draw/SketchCircleDrawingHandler';
import {SketchRectangleCommand, SketchRectangleDrawingHandler} from '../draw/SketchRectangleDrawingHandler';
import {SketchEllipseCommand, SketchEllipseDrawingHandler} from '../draw/SketchEllipseDrawingHandler';
import {SketchSlotCommand, SketchSlotDrawingHandler} from '../draw/SketchSlotDrawingHandler';
import type {EntityPatch} from '../history/types';
import {SketchArcCommand, SketchArcDrawingHandler} from '../draw/SketchArcDrawingHandler';
import {SketchPolylineDrawingHandler} from '../draw/SketchPolylineDrawingHandler';
import {SketchPolygonCommand, SketchPolygonDrawingHandler} from '../draw/SketchPolygonDrawingHandler';
import {SketchPolygonRelationService} from '../sketch/polygon/SketchPolygonRelationService';
import {SketchCircularLinePatternBuilder} from '../sketch/circular-pattern/SketchCircularLinePatternBuilder';
import {SketchSplineCommand, SketchSplineDrawingHandler} from '../draw/SketchSplineDrawingHandler';
import {SketchStyleSplineDrawingHandler} from '../draw/SketchStyleSplineDrawingHandler';
import {
    PointsMaterialFactory,
    //------ 草图线统一深度策略 开始------
    SketchLineMaterialDepthPolicy,
    //------ 草图线统一深度策略 结束------
} from '../material';
import {MidpointPointSnapStrategy, QuadrantPointSnapStrategy, PointSnapRequest, PointSnapResult, PointSnapStrategyType, PointHorizontalVerticalPointSnapStrategy} from './pointSnap';
import {QuickSnapMode} from './pointSnap/QuickSnapMode';
import {SketchPointObject} from '../mesh/sketch';
import {isSketchPointObject} from '../mesh/guards/isSketchPointObject';
import {scheduleDisposableResourceDisposal, scheduleDisposableResourcesDisposal} from '../utils';
import { PointerNearbyLabelPosition } from './PointerNearbyLabelManager';
import {isStaleWorkerResponseError} from '../worker/StaleWorkerResponseError';

//------ 草图绘制命令延迟解析 开始------
/** 草图绘制命令集合缓存。 */
let sketchDrawingCommandSetCache: Set<string> | null = null;

/**
 * 获取草图绘制命令集合。
 * 说明：
 * 1. 使用惰性初始化，避免模块加载阶段触发绘制管理器与各 Handler 的循环依赖；
 * 2. 首次调用时再读取各命令枚举，此时相关模块已完成初始化；
 * 3. 初始化后复用缓存，避免重复创建集合。
 * @returns 草图绘制命令集合。
 */
function getSketchDrawingCommandSet(): Set<string> {
    if (sketchDrawingCommandSetCache) {
        return sketchDrawingCommandSetCache;
    }

    sketchDrawingCommandSetCache = new Set<string>([
        ...Object.values(SketchLineCommand),
        ...Object.values(SketchRectangleCommand),
        ...Object.values(SketchCircleCommand),
        ...Object.values(SketchArcCommand),
        ...Object.values(SketchEllipseCommand),
        ...Object.values(SketchSlotCommand),
        ...Object.values(SketchPolygonCommand),
        ...Object.values(SketchPointCommand),
        ...Object.values(SketchSplineCommand),
    ]);
    return sketchDrawingCommandSetCache;
}
//------ 草图绘制命令延迟解析 结束------

/**
 * 判断命令是否为草图绘制命令。
 * @param command 命令字符串。
 * @returns 是否为草图绘制命令。
 */
export function isSketchDrawingCommand(command: string): boolean {
    return getSketchDrawingCommandSet().has(command);
}

/**
 * 草图绘制处理器管理器。
 * 说明：
 * 1. 负责处理器注册、查询与事件分发；
 * 2. 通过工具类型映射到具体处理器，避免渲染器中堆积分支。
 */
export class SketchDrawingManager {
    /** 草图绘制状态。 */
    public isDrawing: boolean = false;
    /** 当前草图工具。 */
    public currentTool: SketchDrawingTool | null = null;
    /** 草图预览点。 */
    public currentPreviewPoint: THREE.Vector3 | null = null;
    /** 预览版本号。 */
    public previewVersion: number = 0;

    /** 草图预览线。 */
    public previewLine: THREE.Line | null = null;
    /** 草图预览圆。 */
    public previewCircle: THREE.Line | null = null;
    /** 草图预览矩形。 */
    public previewRect: THREE.Line | null = null;
    /** 草图预览点集。 */
    public previewPoints: THREE.Group | null = null;
    /** 草图额外预览线。 */
    public previewExtraLine: THREE.Line | null = null;
    /** 草图预览构造线集合。 */
    public previewConstructionLines: THREE.Line[] = [];
    /** 草图预览半径标签。 */
    public previewRadiusLabel: CSS2DObject | null = null;
    /** 草图预览更新定时器。 */
    public previewUpdateTimer: number | null = null;
    /** 视图控制器引用。 */
    public controls: OrbitControls | null = null;

    /** 最近一次捕捉结果，用于 mouseDown 时直接复用，避免重复计算。 */
    private _lastSnapResult: PointSnapResult | null = null;
    /**
     * 异步运行态历史会话深度。
     *
     * 说明：样条、多段线等工具在双击/闭合后会异步生成实体。生成完成前如果继续接收下一次点击，
     * 旧处理器可能同时清空点集和提交历史，导致绘制状态错乱甚至鼠标事件被持续吞掉。
     */
    private activeRuntimeHistorySessionDepth: number = 0;
    /** 当前点局部坐标缓存。 */
    private readonly currentPointLocalBuffer = new THREE.Vector2();
    /** 交点局部坐标缓存。 */
    private readonly intersectionLocalBuffer = new THREE.Vector2();
    /** 交点世界坐标缓存。 */
    private readonly intersectionWorldBuffer = new THREE.Vector3();

    /** 标签开关。 */
    public labelsEnabled: boolean = false;

    /** 工具到处理器的注册表。 */
    public readonly handlerMap: Map<SketchDrawingTool, SketchDrawingHandler> = new Map();
    /** 内置处理器是否已注册。 */
    public hasRegisteredBuiltInHandlers: boolean = false;
    /** 草图点绘制处理器实例。 */
    public readonly sketchPointDrawingHandler: SketchPointDrawingHandler;
    /** 草图线绘制处理器实例。 */
    public readonly sketchLineDrawingHandler: SketchLineDrawingHandler;
    /** 草图圆绘制处理器实例。 */
    public readonly sketchCircleDrawingHandler: SketchCircleDrawingHandler;
    /** 草图矩形绘制处理器实例。 */
    public readonly sketchRectangleDrawingHandler: SketchRectangleDrawingHandler;
    /** 草图椭圆绘制处理器实例。 */
    public readonly sketchEllipseDrawingHandler: SketchEllipseDrawingHandler;
    /** 草图槽预览处理器实例。 */
    public readonly sketchSlotDrawingHandler: SketchSlotDrawingHandler;
    /** 草图圆弧预览处理器实例。 */
    public readonly sketchArcDrawingHandler: SketchArcDrawingHandler;
    /** 草图多段线预览处理器实例。 */
    public readonly sketchPolylineDrawingHandler: SketchPolylineDrawingHandler;
    /** 草图多边形预览处理器实例。 */
    public readonly sketchPolygonDrawingHandler: SketchPolygonDrawingHandler;
    /** 草图样条族预览处理器实例。 */
    public readonly sketchSplineDrawingHandler: SketchSplineDrawingHandler;
    /** 草图样式样条预览处理器实例。 */
    public readonly sketchStyleSplineDrawingHandler: SketchStyleSplineDrawingHandler;
    /** 鼠标按下事件订阅。 */
    public mouseDownSubscription: Subscription | null = null;
    /** 鼠标移动事件订阅。 */
    public mouseMoveSubscription: Subscription | null = null;
    //------ 切线弧双击结束 开始------
    /** 鼠标双击事件订阅。 */
    public doubleClickSubscription: Subscription | null = null;
    //------ 切线弧双击结束 结束------
    public viewer: Viewer;

    /**
     * 构造函数（仅支持处理器分发模式）。
     * @param viewer
     */
    constructor(viewer: Viewer) {

        this.sketchPointDrawingHandler = new SketchPointDrawingHandler(this);
        this.sketchLineDrawingHandler = new SketchLineDrawingHandler(this);
        this.sketchCircleDrawingHandler = new SketchCircleDrawingHandler(this);
        this.sketchRectangleDrawingHandler = new SketchRectangleDrawingHandler(this);
        this.sketchEllipseDrawingHandler = new SketchEllipseDrawingHandler(this);
        this.sketchSlotDrawingHandler = new SketchSlotDrawingHandler(this);
        this.sketchArcDrawingHandler = new SketchArcDrawingHandler(this);
        this.sketchPolylineDrawingHandler = new SketchPolylineDrawingHandler(this);
        const sketchPolygonRelationService = new SketchPolygonRelationService(
            viewer.sketchRelationCreationService,
        );
        this.sketchPolygonDrawingHandler = new SketchPolygonDrawingHandler(
            this,
            sketchPolygonRelationService,
            new SketchCircularLinePatternBuilder(),
        );
        this.sketchSplineDrawingHandler = new SketchSplineDrawingHandler(this);
        this.sketchStyleSplineDrawingHandler = new SketchStyleSplineDrawingHandler(this);

        this.viewer = viewer;
    }

    /**
     * 设置标签开关。
     * @param enabled 是否启用标签。
     */
    setLabelsEnabled(enabled: boolean): void {
        this.labelsEnabled = enabled;
    }

    /**
     * 开始草图绘制。
     * @param tool 工具类型。
     */
    startDrawing(tool: SketchDrawingTool): void {

        const renderer = this.viewer

        this.resetAllHandlerStates();

        this.isDrawing = true;
        this.currentTool = tool;
        this.currentPreviewPoint = null;
        this.previewVersion += 1;

        if (renderer.container) {
            renderer.container.style.cursor = '';
        }

        renderer.suppressSketchEndpointLayerDuringLineDrawing = tool === SketchDrawingTool.Line;
        if (!renderer.suppressSketchEndpointLayerDuringLineDrawing) {
            // todo
        }

        this.setLastLineClickTimestamp(0);

        const handler = this.handlerMap.get(tool);
        handler?.onActivate(tool);

        //------ 草图绘制状态切换 开始------
        // 统一通过 Viewer 模式驱动交互类与视觉状态类切换。
        renderer.setMode(ViewerMode.SketchDrawing);
        // 工具激活可能只改变临时点与端点层；按需渲染不能依赖下一次相机或 hover 事件。
        renderer.requestRender();
        //------ 草图绘制状态切换 结束------


    }

    /**
     * 停止草图绘制。
     *
     * @param options 停止选项。
     * @param options.publishCompletion 是否发布普通绘制完成语义；资源销毁时必须关闭，避免已释放 Viewer 回写宿主模式。
     */
    stopDrawing(options: { publishCompletion?: boolean } = {}): void {
        const renderer = this.viewer;

        const activeTool = this.currentTool;
        const hadActiveDrawingSession = this.isDrawing || activeTool !== null;
        this.isDrawing = false;
        this.currentTool = null;
        this.currentPreviewPoint = null;

        // 停止后使在途曲线预览失效，避免旧 Worker 结果重新挂回已清理的预览。
        this.invalidatePreviewVersion();
        this.resetAllHandlerStates();

        if (renderer.container) {
            renderer.container.style.cursor = 'default';
        }

        this.clearDrawingPreview();
        // 广播清除所有策略的预览状态（引导线等场景对象）。
        this.viewer.pointSnapManager?.clearAllPreviews();
        //------ 绘制停止捕捉指示器清理 开始------
        // 统一在停止绘制时清理捕捉图标与中点/象限点可见状态，
        // 使 Esc 取消、工具切换、外部调用 stopDrawing 三条链路行为一致。
        this.cleanupSnapIndicator();
        //------ 绘制停止捕捉指示器清理 结束------
        // 重置普通点捕捉状态并隐藏指示器。
        this._lastSnapResult = null;
        if (activeTool === SketchDrawingTool.Point) {
            // 清理最新创建的点状态（从 Viewer.clearLatestCreatedPointState 下沉）
            if (renderer.latestCreatedPointObject) {
                renderer.interactionStateManager?.snapshotOriginalMaterial(renderer.latestCreatedPointObject);
                renderer.latestCreatedPointObject = null;
            }
            // 画面请求已由统一预览清理入口登记，本同步栈中的材质恢复会进入同一帧。
        }
        if (activeTool === SketchDrawingTool.Line) {
            renderer.suppressSketchEndpointLayerDuringLineDrawing = false;
        }
        renderer.setControlsEnabled(true);
        if (options.publishCompletion !== false && hadActiveDrawingSession) {
            //------ 草图绘制状态切换 开始------
            // 只有真实绘制会话的普通结束才回到草图默认模式；dispose 只释放资源，不发布业务状态。
            renderer.setMode(ViewerMode.Sketch);
            //------ 草图绘制状态切换 结束------
            if (renderer.drawingFinishedCallback) {
                renderer.drawingFinishedCallback();
            }
        }

    }

    /**
     * 完成当前草图绘制命令。
     * 说明：
     * 1. 该入口用于“完成”按钮、Enter 等显式完成动作；
     * 2. 多点曲线工具需要先提交当前曲线，再退出绘制态；
     * 3. 固定点数工具或点数不足的多点工具直接停止绘制，避免 UI 已退出但底层仍继续接收点击。
     */
    public async finishCurrentDrawing(): Promise<void> {
        const activeTool = this.currentTool;
        if (!activeTool) {
            return;
        }

        if (activeTool === SketchDrawingTool.Polyline) {
            if (this.sketchPolylineDrawingHandler.drawingPoints.length < 2) {
                this.stopDrawing();
                return;
            }
            await this.sketchPolylineDrawingHandler.finalizePolylineByHandler({stopDrawing: true});
            return;
        }

        if (activeTool === SketchDrawingTool.Spline) {
            if (this.sketchSplineDrawingHandler.drawingPoints.length < 2) {
                this.stopDrawing();
                return;
            }
            await this.sketchSplineDrawingHandler.finishMultiPointDrawing({stopDrawing: true});
            return;
        }

        if (activeTool === SketchDrawingTool.StyleSpline) {
            if (this.sketchStyleSplineDrawingHandler.drawingPoints.length < 2) {
                this.stopDrawing();
                return;
            }
            await this.sketchStyleSplineDrawingHandler.finishMultiPointDrawing({stopDrawing: true});
            return;
        }

        this.stopDrawing();
    }

    /**
     * @description 统一重置所有绘制处理器状态，委托给各处理器自身的 resetState() 方法。
     */
    private resetAllHandlerStates(): void {
        this.handlerMap.forEach((handler) => {
            handler.resetState();
        });
    }

    /**
     * 是否处于草图绘制状态。
     * @returns 是否处于草图绘制状态。
     */
    isDrawingActive(): boolean {
        return this.isDrawing;
    }

    /**
     * 在会话参考点被重建或销毁前清除过期捕捉命中。
     *
     * @param points 即将离开场景的会话参考点。
     */
    public clearSessionReferencePointSnapState(points: readonly SketchPointObject[]): void {
        const targetObject = this._lastSnapResult?.targetObject ?? null;
        if (!targetObject || !points.includes(targetObject as SketchPointObject)) {
            return;
        }

        this._lastSnapResult = null;
        this.cleanupSnapIndicator();
    }

    /**
     * 获取当前草图工具。
     * @returns 当前工具。
     */
    getCurrentTool(): SketchDrawingTool | null {
        return this.currentTool;
    }

    /**
     * 获取草图预览点。
     * @returns 预览点。
     */
    getPreviewPoint(): THREE.Vector3 | null {
        return this.currentPreviewPoint ? this.currentPreviewPoint.clone() : null;
    }

    /**
     * 设置草图预览点。
     * @param point 预览点。
     */
    setPreviewPoint(point: THREE.Vector3 | null): void {
        this.currentPreviewPoint = point ? point.clone() : null;
    }

    /**
     * 使当前所有在途预览任务立即失效。
     * 说明：
     * 1. 异步预览处理器会在回调落地前比对版本号；
     * 2. 递增版本号后，旧版本对应的 Worker 回调将被直接丢弃；
     * 3. 该方法不负责场景对象清理，仅负责失效控制。
     */
    public invalidatePreviewVersion(): void {
        this.previewVersion += 1;
    }

    /**
     * 获取线绘制双击阈值。
     * @returns 阈值（毫秒）。
     */
    getLineDoubleClickThreshold(): number {
        return this.viewer.doubleClickThreshold;
    }

    /**
     * 获取最近一次点击时间戳。
     * @returns 时间戳（毫秒）。
     */
    getLastLineClickTimestamp(): number {
        return this.viewer.lastClickTime;
    }

    /**
     * 设置最近一次点击时间戳。
     * @param timestamp 时间戳（毫秒）。
     */
    setLastLineClickTimestamp(timestamp: number): void {
        this.viewer.lastClickTime = timestamp;
    }

    /**
     * 获取最近创建线段快照。
     * @returns 快照信息。
     */
    public undoLastDrawingPoint(): boolean {
        const activeTool = this.currentTool;
        if (!activeTool) {
            return false;
        }
        const handler = this.handlerMap.get(activeTool);
        if (!handler) {
            return false;
        }
        if (!handler.undoLastDrawingPoint()) {
            return false;
        }
        this.currentPreviewPoint = null;
        this.clearDrawingPreview();
        // 预览清理入口已统一登记重绘，不额外启动同步渲染。
        return true;
    }

    /**
     * 在模型历史恢复前清理绘制期临时状态。
     *
     * 说明：undo/redo 会整体替换草图组子树。绘制处理器、捕捉管理器和预览对象如果继续持有
     * 被替换的 Object3D，会在下一次 mousemove/click 访问已释放对象，表现为界面卡死或绘制无响应。
     * 这里只清临时引用并保留当前绘制工具，使用户在模型 undo 后仍可继续当前草图命令。
     */
    public prepareForModelHistoryRestore(): void {
        const activeTool = this.currentTool;

        this.previewVersion += 1;
        this.currentPreviewPoint = null;
        this._lastSnapResult = null;
        this.setLastLineClickTimestamp(0);
        this.clearDrawingPreview();
        this.viewer.pointSnapManager?.clearAllPreviews();
        this.cleanupSnapIndicator();
        this.resetAllHandlerStates();

        if (activeTool) {
            this.currentTool = activeTool;
            this.isDrawing = true;
            this.handlerMap.get(activeTool)?.onActivate(activeTool);
        }
    }

    /**
     * 构造统一点捕捉请求。
     * 说明：
     * 1. 点击回退捕捉与鼠标移动预览捕捉必须复用同一份请求构造逻辑；
     * 2. 这样可以保证样条/样式样条“仅首点可捕捉”的临时白名单在两条链路中行为一致；
     * 3. 其它工具仍通过空白名单运行，不引入额外捕捉噪音。
     * @param worldPoint 当前世界坐标。
     * @param screenPos 当前屏幕坐标。
     * @param options 可选附加参数。
     * @returns 统一点捕捉请求。
     */
    private buildPointSnapRequest(
        worldPoint: THREE.Vector3,
        screenPos: THREE.Vector2,
        options?: {
            drawingFirstPoint?: THREE.Vector3 | null;
            referenceDirection?: THREE.Vector3 | null;
        },
    ): PointSnapRequest {
        return {
            screenPosition: screenPos,
            worldPosition: worldPoint,
            candidateObjects: [],
            hoveredObject: this.viewer?.entitySelectionManager?.hoveredObject ?? null,
            transientPointObjects: this.resolveTransientPointSnapObjects(),
            drawingFirstPoint: options?.drawingFirstPoint ?? null,
            referenceDirection: options?.referenceDirection ?? null,
        };
    }

    /**
     * 解析可参与控制点复用的捕捉目标点。
     * 说明：
     * 1. 正式草图点允许继续透传给各绘制处理器复用；
     * 2. 绘制期临时参考点只提供位置参考，不允许成为共享控制点；
     * 3. 非草图点对象统一返回 null。
     * @param targetObject 捕捉结果中的目标对象。
     * @returns 可复用的正式草图点对象；否则返回 null。
     */
    private resolveSnappedTargetPointForReuse(targetObject: THREE.Object3D | null | undefined): SketchPointObject | null {
        if (!isSketchPointObject(targetObject)) {
            return null;
        }
        // 会话原点和模型投影点只提供捕捉坐标，不能成为正式草图实体的共享控制点。
        if (this.viewer.sketchSessionReferencePointManager?.owns(targetObject)) {
            return null;
        }
        return targetObject;
    }

    /**
     * 注册绘制处理器。
     * @param handler 待注册处理器。
     */
    registerHandler(handler: SketchDrawingHandler): void {
        const tools = Array.isArray(handler.tool) ? handler.tool : [handler.tool];
        tools.forEach((tool) => {
            // 禁止重复注册，避免同一工具存在不确定处理器。
            if (this.handlerMap.has(tool)) {
                throw new Error(`绘制处理器重复注册: ${tool}`);
            }
            this.handlerMap.set(tool, handler);
        });
    }


    /**
     * 注册内置草图绘制处理器。
     * 说明：
     * 1. 仅允许注册一次，避免重复注册导致冲突；
     * 2. 由管理器统一托管处理器生命周期。
     */
    registerBuiltInHandlers(): void {
        if (this.hasRegisteredBuiltInHandlers) {
            return;
        }
        this.registerHandler(this.sketchPointDrawingHandler);
        this.registerHandler(this.sketchLineDrawingHandler);
        this.registerHandler(this.sketchCircleDrawingHandler);
        this.registerHandler(this.sketchRectangleDrawingHandler);
        this.registerHandler(this.sketchEllipseDrawingHandler);
        this.registerHandler(this.sketchArcDrawingHandler);
        this.registerHandler(this.sketchSlotDrawingHandler);
        this.registerHandler(this.sketchPolylineDrawingHandler);
        this.registerHandler(this.sketchPolygonDrawingHandler);
        this.registerHandler(this.sketchSplineDrawingHandler);
        this.registerHandler(this.sketchStyleSplineDrawingHandler);
        this.hasRegisteredBuiltInHandlers = true;
    }

    /**
     * 按工具解析绘图提示，委托给对应处理器的 getDrawingHint() 方法。
     * @param tool 当前工具。
     * @returns 提示文本。
     */
    resolveDrawingHintByTool(tool: string): string {
        const handler = this.handlerMap.get(tool as SketchDrawingTool);
        if (handler) {
            return handler.getDrawingHint(tool as SketchDrawingTool);
        }
        return '绘制中... | ESC取消';
    }

    /**
     * 处理鼠标按下事件。
     * @param tool 当前工具类型。
     * @param payload 鼠标按下载荷。
     * @throws 当当前绘制工具没有注册处理器时抛出错误。
     */
    handleMouseDown(
        tool: SketchDrawingTool,
        payload: SketchDrawingMouseDownPayload,
    ): void {
        // 未注册处理器直接抛错，禁止静默兜底。
        // 异步完成提交未闭合前不接收新点击，避免旧处理器和新点击同时改写点集。
        if (this.isRuntimeHistorySessionActive()) {
            return;
        }
        const handler = this.handlerMap.get(tool);
        if (!handler) {
            throw new Error(`未注册绘制处理器: ${tool}`);
        }
        if (!this.shouldCommitSketchRuntimeHistoryOnMouseDown(handler)) {
            handler.handleMouseDown(payload);
            // 首点、辅助点和切线弧引导线不产生历史事务，但仍然改变画面。
            this.viewer.requestRender();
            return;
        }
        void this.executeActiveSketchRuntimeHistorySession({
            label: `草图绘制：${tool}`,
            commandType: `sketch.runtime.draw.${tool}`,
            work: async () => {
                handler.handleMouseDown(payload);
            },
            // 绘制处理器产出的领域补丁（如槽口自动尺寸）并入本笔事务，保证一步撤销。
            // 可选调用：处理器替身（测试夹具）可能没有该方法，缺失即视为无附加补丁。
            resolveExtraPatches: () => handler.takePendingHistoryPatches?.() ?? [],
        }).catch((error) => {
            this.reportSketchRuntimeHistorySessionFailure(error);
        });
    }

    //------ 切线弧双击结束 开始------
    /**
     * 处理鼠标双击事件。
     * 说明：独立消费浏览器 dblclick，避免连续绘制过程中的普通 mouseDown.detail 被误判为完成。
     * @param tool 当前工具类型。
     * @param payload 鼠标双击载荷。
     * @throws 当当前绘制工具没有注册处理器时抛出错误。
     */
    handleDoubleClick(
        tool: SketchDrawingTool,
        payload: SketchDrawingMouseDownPayload,
    ): void {
        if (this.isRuntimeHistorySessionActive()) {
            return;
        }
        const handler = this.handlerMap.get(tool);
        if (!handler) {
            throw new Error(`未注册绘制处理器: ${tool}`);
        }
        handler.handleDoubleClick(payload);
        // 双击可能仅收起临时线，异步正式提交则在历史会话结束时再次登记最终画面。
        this.viewer.requestRender();
    }
    //------ 切线弧双击结束 结束------

    /**
     * 判断当前点击是否会把临时绘制状态提交为正式草图实体。
     *
     * 说明：
     * 1. 草图运行态历史会克隆当前草图组，不能放在首点、预览点这类高频交互路径上；
     * 2. 固定点数工具只有在本次点击达到所需点数时才会创建实体，此时才捕获 before/after；
     * 3. 样条这类不定点工具的提交入口在 finishCurrentDrawing/双击完成链路中，普通落点不进入全局模型历史。
     *
     * @param handler 当前草图绘制处理器。
     * @returns 当前 mouseDown 是否应生成模型历史事务。
     */
    private shouldCommitSketchRuntimeHistoryOnMouseDown(handler: SketchDrawingHandler): boolean {
        const requiredPointCount = handler.getRequiredPointCount();
        if (!Number.isFinite(requiredPointCount)) {
            return false;
        }
        return handler.drawingPoints.length + 1 >= requiredPointCount;
    }

    /**
     * 在当前活动草图组上执行异步运行态历史会话。
     *
     * 说明：
     * 1. 多段线、样条、样式样条等不定点工具不能在每次落点时捕获整组快照；
     * 2. 它们真正创建实体的时机是双击、闭合或 Enter 完成，因此事务边界必须放在完成入口；
     * 3. work 抛错时通过会话 cancel 恢复 before，避免半提交实体留在场景里。
     *
     * @param input 历史会话输入。
     * @returns work 的执行结果。
     * @throws 原样抛出工作函数或历史会话提交产生的异常。
     */
    public async executeActiveSketchRuntimeHistorySession<TResult>(input: {
        readonly label: string;
        readonly commandType: string;
        readonly work: () => Promise<TResult>;
        /**
         * 解析随本笔事务一同提交的领域补丁（work 之后调用）。
         *
         * 用于"几何 + 领域数据必须一步撤销"的场景，例如槽口创建时自动添加的尺寸。
         */
        readonly resolveExtraPatches?: () => readonly EntityPatch[];
    }): Promise<TResult> {
        const session = this.viewer.beginActiveSketchRuntimeHistorySession({
            label: input.label,
            commandType: input.commandType,
        });

        this.activeRuntimeHistorySessionDepth += 1;
        try {
            const result = await input.work();
            const extraPatches = input.resolveExtraPatches?.() ?? [];

            await session?.commit(extraPatches.length > 0 ? {extraPatches} : undefined);
            return result;
        } catch (error) {
            session?.cancel();
            throw error;
        } finally {
            this.activeRuntimeHistorySessionDepth -= 1;
            // 成功提交与失败回滚均已完成状态收口，统一绘制最终结果，不改变历史边界。
            this.viewer.requestRender();
        }
    }

    /**
     * 判断当前是否有正在收口的运行态历史会话。
     *
     * @returns 存在异步完成事务时返回 true。
     */
    private isRuntimeHistorySessionActive(): boolean {
        return this.activeRuntimeHistorySessionDepth > 0;
    }

    /**
     * 上报草图运行态历史提交失败。
     *
     * @param error 原始错误。
     */
    private reportSketchRuntimeHistorySessionFailure(error: unknown): void {
        console.error('草图运行态历史提交失败', error);
        this.viewer.drawingNoticeCallback?.('草图历史提交失败，已恢复到操作前状态，请重试。', 'error');
    }

    /**
     * 解析当前激活工具用于方向型捕捉的绘制起点。
     * 说明：
     * 1. 水平/竖直、角度等方向型捕捉应始终基于“当前激活处理器”的首点；
     * 2. 不能再写死为线工具首点，否则多边形、矩形等两点式工具无法参与方向型捕捉；
     * 3. 当当前工具尚未落下首点时返回 null。
     * @param activeTool 当前激活工具。
     * @returns 当前工具首点；不存在时返回 null。
     */
    private resolveDirectionalDrawingFirstPoint(activeTool: SketchDrawingTool): THREE.Vector3 | null {
        const handler = this.handlerMap.get(activeTool);
        if (!handler) {
            return null;
        }
        return handler.drawingPoints[0] ?? null;
    }

    /**
     * 解析当前激活工具用于角度捕捉的参考方向。
     * 说明：
     * 1. 当前仅线工具存在“上一条线方向”这一明确语义；
     * 2. 多边形等其它工具未提供稳定的历史参考方向时，返回 null，角度策略将回退到参考平面水平轴；
     * 3. 这样可以在不改变现有线工具行为的前提下，让多边形正常参与水平/竖直捕捉。
     * @param activeTool 当前激活工具。
     * @returns 参考方向；不存在时返回 null。
     */
    private resolveDirectionalReferenceDirection(activeTool: SketchDrawingTool): THREE.Vector3 | null {
        if (activeTool !== SketchDrawingTool.Line) {
            return null;
        }

        const lastLine = this.sketchLineDrawingHandler.lastCreatedLine;
        if (!lastLine) {
            return null;
        }

        const startWorld = lastLine.startPoint.getPosition();
        const endWorld = lastLine.endPoint.getPosition();
        const direction = new THREE.Vector3().subVectors(startWorld, endWorld);
        if (direction.lengthSq() <= 1e-18) {
            return null;
        }

        return direction.normalize();
    }


    /**
     * 处理预览更新。
     * @param tool 当前工具类型。
     * @param currentPoint 当前预览点。
     * @param currentVersion 预览版本号。
     * @param startPointOverride 起点覆盖（快速相切捕捉阶段二：动态切点替换 drawingPoints[0]）。
     * @throws 当绘制处理器缺失、未实现预览更新，或预览更新抛出非过期响应异常时抛出错误。
     */
    async handlePreview(
        tool: SketchDrawingTool,
        currentPoint: THREE.Vector3,
        currentVersion: number,
        startPointOverride?: THREE.Vector3,
    ): Promise<void> {
        if (this.isRuntimeHistorySessionActive()) {
            return;
        }
        const handler = this.handlerMap.get(tool);
        if (!handler) {
            throw new Error(`未注册绘制处理器: ${tool}`);
        }
        if (!handler.updatePreview) {
            throw new Error(`处理器未实现预览更新: ${tool}`);
        }
        try {
            await handler.updatePreview(currentPoint, currentVersion, tool, startPointOverride);
            //------ 草图线统一深度策略 开始------
            // 各绘制工具完成预览更新后统一收口深度状态，避免处理器自行创建材质时遗漏覆盖层规则。
            this.applyPreviewLineDepthPolicy();
            // 同步工具与 Worker 异步工具都在预览写入完成后登记普通重绘，由宿主帧合并。
            this.viewer.requestRender();
            //------ 草图线统一深度策略 结束------
        } catch (error) {
            /**
             * Worker stale 代表鼠标预览请求返回时草图/历史 epoch 已被新操作推进。
             * 预览是可丢弃派生状态，必须静默放弃旧结果，避免退出草图、Undo/Redo 后旧异步响应污染控制台或场景。
             */
            if (isStaleWorkerResponseError(error)) {
                return;
            }
            throw error;
        }
    }


    /**
     * 启动事件监听。
     * 说明：
     * 1. 事件唯一来源为 EventManager 派发的原始鼠标事件；
     * 2. 启动后按当前绘制状态与工具类型路由到对应处理器。
     * @throws 当任一绘制事件订阅已存在时抛出错误。
     */
    start(): void {
        if (this.mouseDownSubscription || this.mouseMoveSubscription || this.doubleClickSubscription) {
            throw new Error('SketchDrawingManager 已启动，禁止重复启动');
        }

        this.mouseDownSubscription = this.viewer.eventManager
            .observe(RawPointerEventType.MouseDown)
            .subscribe((pointerPayload) => {
                // 直接使用类自身持有的当前工具判断
                const activeTool = this.currentTool;
                if (!activeTool) {
                    return;
                }
                if (!this.hasDispatchTarget(activeTool)) {
                    return;
                }
                if (pointerPayload.button !== 0) {
                    return;
                }

                // ------ 点捕捉集成 开始 ------
                // 使用 _lastSnapResult（已在 mousemove 中统一计算）或回退到实时捕捉。
                let snappedPoint: THREE.Vector3 | null = null;
                let snappedTargetPoint = null;
                if (this._lastSnapResult !== null) {
                    snappedPoint = this._lastSnapResult.position.clone();
                    snappedTargetPoint = this.resolveSnappedTargetPointForReuse(this._lastSnapResult.targetObject);
                } else {
                    const rawPoint = this.getDrawingPoint(pointerPayload.nativeEvent);
                    //------ 草图参考面缺失错误日志 开始------
                    if (!rawPoint) {
                        return;
                    }
                    //------ 草图参考面缺失错误日志 结束------
                    const screenPos = this.computeScreenPosition(pointerPayload.nativeEvent);
                    snappedPoint = this.trySnap(rawPoint, screenPos);
                    snappedTargetPoint = this.resolveSnappedTargetPointForReuse(this._lastSnapResult?.targetObject);
                }

                //------ 快速捕捉 one-shot 清除 开始------
                // 相切快速捕捉需要两次点击才能完成一条线：
                //   - 第一次点击：锁定目标曲线，quickSnapMode 保持 Tangent 以继续驱动阶段二；
                //   - 第二次点击：线段完成，此时清除模式。
                // 其他快速捕捉只需一次点击即可，直接清除。
                const snapManager = this.viewer.pointSnapManager;
                const isTangentQuickSnap = snapManager?.quickSnapMode === QuickSnapMode.Tangent;
                if (isTangentQuickSnap) {
                    // 相切快速捕捉：有已记录的第一个点（即第二次点击），才清除。
                    const hasFirstPoint = this.sketchLineDrawingHandler.drawingPoints.length > 0;
                    if (hasFirstPoint) {
                        // 用 startPointOverride（曲线上的切点）替换 drawingPoints[0]，
                        // 使最终绘制的线从切点开始，而非第一次点击时的初始切点。
                        const override = this._lastSnapResult?.startPointOverride;
                        if (override) {
                            this.sketchLineDrawingHandler.drawingPoints[0] = override.clone();
                        }
                        snapManager.setQuickSnapMode(null);
                    }
                    // else: 第一次点击，不清除 quickSnapMode，继续阶段二。
                } else if (snapManager?.quickSnapMode !== null && snapManager?.quickSnapMode !== undefined) {
                    snapManager.setQuickSnapMode(null);
                }
                //------ 快速捕捉 one-shot 清除 结束------

                // ------ 点捕捉集成 结束 ------

                this.handleMouseDown(
                    activeTool,
                    {
                        event: pointerPayload.nativeEvent,
                        snappedPoint,
                        snappedTargetPoint,
                        // 传递单击瞬间仍有效的捕捉结果，供绘制处理器提交自动关系。
                        snapResult: this._lastSnapResult,
                    },
                );
            });

        //------ 切线弧双击结束 开始------
        this.doubleClickSubscription = this.viewer.eventManager
            .observe(RawPointerEventType.DoubleClick)
            .subscribe((pointerPayload) => {
                const activeTool = this.currentTool;
                if (!activeTool || !this.hasDispatchTarget(activeTool)) {
                    return;
                }
                if (pointerPayload.button !== 0) {
                    return;
                }
                this.handleDoubleClick(
                    activeTool,
                    {event: pointerPayload.nativeEvent},
                );
            });
        //------ 切线弧双击结束 结束------

        this.mouseMoveSubscription = this.viewer.eventManager
            .observe(RawPointerEventType.MouseMove)
            .subscribe((pointerPayload) => {
                // 直接使用类自身持有的当前工具和渲染器
                const activeTool = this.currentTool;
                if (!activeTool || !this.hasDispatchTarget(activeTool)) {
                    return;
                }
                // ------ 点捕捉集成 开始 ------
                const rawPoint = this.getDrawingPoint(pointerPayload.nativeEvent);
                // getDrawingPoint 可能因射线不相交基准面而返回 undefined，此时跳过捕捉。
                if (!rawPoint) {
                    return;
                }
                // 计算屏幕像素坐标用于点捕捉距离判定。
                const screenPos = this.computeScreenPosition(pointerPayload.nativeEvent);

                //------ 方向型捕捉集成 开始------
                const snapManager = this.viewer.pointSnapManager;
                // 构建包含绘制起点的捕捉请求，方向型策略（切线/垂线/平行/水平竖直/角度）需要此字段。
                const firstPoint = this.resolveDirectionalDrawingFirstPoint(activeTool);
                const referenceDirection = this.resolveDirectionalReferenceDirection(activeTool);

                const directionalRequest = this.buildPointSnapRequest(rawPoint, screenPos, {
                    drawingFirstPoint: firstPoint,
                    referenceDirection,
                });

                // 执行捕捉求解（策略内部更新引导线并返回最优捕捉结果）。
                const directionalResult = snapManager?.resolve(directionalRequest) ?? null;
                this._lastSnapResult = directionalResult;

                let currentPoint: THREE.Vector3;
                const isDirectionalSnap = directionalResult !== null && (
                    directionalResult.strategyType === PointSnapStrategyType.Tangent
                    || directionalResult.strategyType === PointSnapStrategyType.Perpendicular
                    || directionalResult.strategyType === PointSnapStrategyType.Parallel
                    || directionalResult.strategyType === PointSnapStrategyType.HorizontalVertical
                    || directionalResult.strategyType === PointSnapStrategyType.Angle
                );

                if (isDirectionalSnap) {
                    // 方向型捕捉命中：直接使用投影点，指示器使用结果中的图标（含 H/V 图标覆盖）。
                    currentPoint = directionalResult!.position;
                    this.showSnapIndicator(directionalResult!);
                    this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
                    //------ 角度标签显示 开始------
                    // 无论哪种方向型捕捉命中，都额外检查角度捕捉是否也命中。
                    // 这样在 HV（优先级更高）赢得图标的同时，角度标签也能同步显示。
                    // 例如：水平方向 = 180°，HV 显示水平图标，角度标签同时显示 "180°"。
                    const angleStrategy = snapManager?.getStrategy(PointSnapStrategyType.Angle);
                    const angleResult = (angleStrategy?.enabled === true)
                        ? angleStrategy.resolve(directionalRequest)
                        : null;
                    if (angleResult !== null && angleResult?.snapAngleDeg !== undefined) {
                        this.showAngleSnapLabel(currentPoint, angleResult.snapAngleDeg);
                    } else {
                        this.hideAngleSnapLabel();
                    }
                    //------ 角度标签显示 结束------
                    //------ HV 方向捕捉 + 点 HV 追踪双重命中 开始------
                    // 当绘制方向捕捉为水平/竖直，同时与点 HV 追踪存在垂直方向的追踪线时，
                    // 计算两条约束线的交叉点，并显示双图标（主：绘制方向图标，次：追踪方向图标）。
                    if (directionalResult!.strategyType === PointSnapStrategyType.HorizontalVertical) {
                        const hvPointStrategy = snapManager?.getStrategy(PointSnapStrategyType.PointHorizontalVertical);
                        if (hvPointStrategy instanceof PointHorizontalVerticalPointSnapStrategy && hvPointStrategy.enabled) {
                            const hvPointState = hvPointStrategy.getActiveState();
                            const referencePlane = this.viewer?.currentReferencePlane ?? null;
                            if (referencePlane) {
                                const isHvHorizontal = directionalResult!.indicatorIconOverride?.includes('horizontal') ?? false;
                                const isHvVertical = directionalResult!.indicatorIconOverride?.includes('vertical') ?? false;
                                // 将当前 currentPoint 投影到参考平面，获取已固定的轴坐标。
                                const currentLocal = referencePlane.worldToLocalTo(currentPoint, this.currentPointLocalBuffer);
                                let intersectionPoint: THREE.Vector3 | null = null;
                                let secondaryIcon: string | null = null;
                                if (isHvHorizontal && hvPointState.hasVertical && hvPointState.vertRefLocalX !== null) {
                                    // 绘制方向水平（Y 固定为 currentLocal.y）+ 追踪线竖直（X 固定为 vertRefLocalX）。
                                    intersectionPoint = referencePlane.localToWorldTo(
                                        this.intersectionLocalBuffer.set(hvPointState.vertRefLocalX, currentLocal.y),
                                        this.intersectionWorldBuffer,
                                    );
                                    secondaryIcon = '/textures/sketch/snap-vertical.svg';
                                } else if (isHvVertical && hvPointState.hasHorizontal && hvPointState.horzRefLocalY !== null) {
                                    // 绘制方向竖直（X 固定为 currentLocal.x）+ 追踪线水平（Y 固定为 horzRefLocalY）。
                                    intersectionPoint = referencePlane.localToWorldTo(
                                        this.intersectionLocalBuffer.set(currentLocal.x, hvPointState.horzRefLocalY),
                                        this.intersectionWorldBuffer,
                                    );
                                    secondaryIcon = '/textures/sketch/snap-horizontal.svg';
                                }
                                if (intersectionPoint !== null && secondaryIcon !== null) {
                                    // 使用交叉点替换 currentPoint 并更新 _lastSnapResult。
                                    currentPoint = intersectionPoint.clone();
                                    this._lastSnapResult = {
                                        strategyType: PointSnapStrategyType.HorizontalVertical,
                                        position: currentPoint,
                                        indicatorPosition: currentPoint,
                                        targetObject: null,
                                        weight: 0,
                                        // 双重命中只调整终点坐标，主 H/V 捕捉的关系语义必须保留。
                                        suggestedRelationKind: directionalResult!.suggestedRelationKind,
                                    };
                                    // 更新主图标位置（交叉点），显示次图标。
                                    this.showSnapIndicator(this._lastSnapResult, directionalResult!.indicatorIconOverride ?? undefined);
                                    this.viewer.pointerNearbyLabelManager.showPointSnapSecondaryIndicator(secondaryIcon);
                                } else {
                                    // 无垂直方向追踪（方向平行或无追踪）：不显示次图标，避免出现两个相同方向图标。
                                    this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
                                }
                            }
                        }
                    }
                    //------ HV 方向捕捉 + 点 HV 追踪双重命中 结束------
                } else {
                    // 非方向型捕捉命中时清除角度标签。
                    this.hideAngleSnapLabel();
                    // 非方向型捕捉：使用普通点捕捉结果。
                    if (directionalResult !== null) {
                        currentPoint = directionalResult.position;
                        if (directionalResult.showIndicator !== false) {
                            this.showSnapIndicator(directionalResult);
                        }
                    } else {
                        currentPoint = rawPoint;
                        this.hideSnapIndicator();
                    }
                    // 与点水平/竖直追踪：普通捕捉后叠加显示 H/V 图标。
                    // 只要存在单轴或双轴命中，都要将当前点收口到策略计算出的投影点。
                    const pointHvStrategy = snapManager?.getStrategy(PointSnapStrategyType.PointHorizontalVertical);
                    currentPoint = this.applyPointHorizontalVerticalTrackingOverlay(
                        currentPoint,
                        directionalResult,
                        pointHvStrategy instanceof PointHorizontalVerticalPointSnapStrategy && pointHvStrategy.enabled
                            ? pointHvStrategy.getActiveState()
                            : null,
                    );
                }
                //------ 方向型捕捉集成 结束------
                // ------ 点捕捉集成 结束 ------

                this.previewVersion += 1;
                const currentVersion = this.previewVersion;
                // 快速相切捕捉阶段二：传递 startPointOverride 使预览线从动态切点开始。
                const previewStartOverride = this._lastSnapResult?.startPointOverride ?? undefined;
                void this.handlePreview(activeTool, currentPoint.clone(), currentVersion, previewStartOverride);
            });

    }

    /**
     * 停止事件监听。
     */
    stop(): void {
        if (!this.mouseDownSubscription && !this.mouseMoveSubscription && !this.doubleClickSubscription) {
            return;
        }
        if (this.mouseDownSubscription) {
            this.mouseDownSubscription.unsubscribe();
            this.mouseDownSubscription = null;
        }
        if (this.mouseMoveSubscription) {
            this.mouseMoveSubscription.unsubscribe();
            this.mouseMoveSubscription = null;
        }
        if (this.doubleClickSubscription) {
            this.doubleClickSubscription.unsubscribe();
            this.doubleClickSubscription = null;
        }
    }

    //------ 草图线统一深度策略 开始------
    /**
     * 对当前绘制会话持有的全部临时线应用统一深度策略。
     *
     * 设计说明：
     * 1. 草图圆、圆弧、矩形、样条等处理器会创建不同材质类型，但都通过本管理器持有预览对象；
     * 2. 在调度出口统一处理，可避免逐个修改大型绘制处理器并消除重复配置；
     * 3. 直接处理固定引用并原地遍历构造线数组，避免鼠标移动热路径创建临时容器。
     */
    private applyPreviewLineDepthPolicy(): void {
        this.applyPreviewLineMaterialDepthPolicy(this.previewLine);
        this.applyPreviewLineMaterialDepthPolicy(this.previewCircle);
        this.applyPreviewLineMaterialDepthPolicy(this.previewRect);
        this.applyPreviewLineMaterialDepthPolicy(this.previewExtraLine);

        for (let index = 0; index < this.previewConstructionLines.length; index += 1) {
            this.applyPreviewLineMaterialDepthPolicy(this.previewConstructionLines[index]);
        }
    }

    /**
     * 对单个临时线的材质应用深度策略。
     *
     * @param line 待处理的临时线；不存在时直接返回。
     */
    private applyPreviewLineMaterialDepthPolicy(line: THREE.Line | null | undefined): void {
        if (!line) {
            return;
        }

        if (Array.isArray(line.material)) {
            SketchLineMaterialDepthPolicy.apply(line.material);
            return;
        }
        SketchLineMaterialDepthPolicy.apply(line.material);
    }
    //------ 草图线统一深度策略 结束------

    /**
     * 释放资源。
     */
    dispose(): void {
        this.stop();
        /**
         * Viewer 销毁是资源生命周期边界，不是用户完成绘制。
         * 禁止在这里触发 onDrawingFinished 或把宿主 Store 回写为 Sketch，
         * 否则根文档热切换创建的新 Viewer 会继承旧 Viewer 的草图模式。
         */
        this.stopDrawing({ publishCompletion: false });
        this.clearPreview();
        this.cleanupSnapIndicator();
        this.handlerMap.clear();
        this.hasRegisteredBuiltInHandlers = false;
    }

    /**
     * 判断工具是否存在可分发目标。
     * @param tool 当前工具。
     * @returns 是否可分发。
     */
    public hasDispatchTarget(tool: SketchDrawingTool): boolean {
        return this.handlerMap.has(tool);
    }

    /**
     * 清理草图预览对象。
     */
    public clearPreview(): void {
        // 完整清理与普通清理共用资源释放、辅助线移除和重绘路径，避免两个入口漏项。
        this.clearDrawingPreview();
        if (this.previewUpdateTimer) {
            clearTimeout(this.previewUpdateTimer);
            this.previewUpdateTimer = null;
        }
        this.previewVersion += 1;
    }

    /**
     * 清理草图吸附指示器。
     * 说明：资源由 PointSnapManager 管理，此处仅隐藏标签。
     */
    public cleanupSnapIndicator(): void {
        // 隐藏指示器标签（资源由 PointSnapManager 管理，此处仅隐藏）。
        this.hideSnapIndicator();
        // 重置中点捕捉策略的可见状态，防止中点对象残留在场景中。
        this.resetMidpointStrategy();
        // 重置象限点捕捉策略的可见状态，防止象限点对象残留在场景中。
        this.resetQuadrantStrategy();
    }

    // ------ 点捕捉集成 开始 ------

    /**
     * 将鼠标事件坐标转换为渲染器像素坐标。
     * 说明：与 AbstractPointSnapStrategy.projectWorldToScreen 使用相同坐标系，确保距离计算一致。
     * @param event 原生鼠标事件。
     * @returns 渲染器像素坐标（原点在左上角）。
     */
    private computeScreenPosition(event: MouseEvent): THREE.Vector2 {
        const canvas = this.viewer.renderer.domElement;
        const rect = canvas.getBoundingClientRect();
        const rendererSize = new THREE.Vector2();
        this.viewer.renderer.getSize(rendererSize);
        return new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * rendererSize.x,
            ((event.clientY - rect.top) / rect.height) * rendererSize.y,
        );
    }

    /**
     * 尝试执行点捕捉，更新捕捉指示器，返回最终使用的点位。
     * 说明：
     * 1. pointSnapManager 不可用时直接返回原始点；
     * 2. 有捕捉结果时更新指示器位置和标签文字；
     * 3. 无捕捉结果时隐藏指示器。
     * @param worldPoint 原始鼠标世界坐标。
     * @param screenPos 鼠标屏幕像素坐标。
     * @returns 捕捉后的世界坐标；未命中时返回原始点。
     */
    private trySnap(worldPoint: THREE.Vector3, screenPos: THREE.Vector2): THREE.Vector3 {
        const snapManager = this.viewer.pointSnapManager;
        if (!snapManager) {
            this._lastSnapResult = null;
            this.hideSnapIndicator();
            return worldPoint;
        }

        const request = this.buildPointSnapRequest(worldPoint, screenPos);

        const result = snapManager.resolve(request);
        this._lastSnapResult = result;

        if (result !== null) {
            // 仅在结果允许时显示 CSS 指示器图标（中点边缘悬停阶段不显示）。
            if (result.showIndicator !== false) {
                this.showSnapIndicator(result);
            } else {
                this.hideSnapIndicator();
            }
            return result.position;
        }

        this.hideSnapIndicator();
        return worldPoint;
    }

    //------ 与点水平/竖直投影收口 开始------
    /**
     * 将“与点水平/竖直”追踪状态收口到当前绘制点与指示器。
     * 说明：
     * 1. 普通模式下，PointHorizontalVerticalPointSnapStrategy 只负责计算追踪状态与投影点，不直接返回 PointSnapResult；
     * 2. 真正用于预览与点击确认的落点必须在 SketchDrawingManager 这里统一收口；
     * 3. 只要出现单条或双条 H/V 追踪线，且策略已算出 snapPoint，就应使用该投影点；
     * 4. 端点/中点/中心点/象限点等最高优先级点类命中时，仍保持原主图标语义，不允许被 H/V 图标覆盖。
     * @param currentPoint 当前绘制点。
     * @param directionalResult 当前普通捕捉结果。
     * @param hvState 与点水平/竖直追踪状态；为 null 表示当前无可用状态。
     * @returns 收口后的当前绘制点。
     */
    private applyPointHorizontalVerticalTrackingOverlay(
        currentPoint: THREE.Vector3,
        directionalResult: PointSnapResult | null,
        hvState: {
            hasHorizontal: boolean;
            hasVertical: boolean;
            snapPoint: THREE.Vector3 | null;
            horzRefLocalY: number | null;
            vertRefLocalX: number | null;
        } | null,
    ): THREE.Vector3 {
        if (hvState === null || (!hvState.hasHorizontal && !hvState.hasVertical)) {
            this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
            return currentPoint;
        }

        const hasTopPriorityPointIndicator = directionalResult !== null && (
            directionalResult.strategyType === PointSnapStrategyType.EndpointOrigin
            || directionalResult.strategyType === PointSnapStrategyType.Center
            || directionalResult.strategyType === PointSnapStrategyType.Quadrant
            || directionalResult.strategyType === PointSnapStrategyType.Midpoint
        );

        if (hasTopPriorityPointIndicator) {
            this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
            return currentPoint;
        }

        //------ 与点水平/竖直单轴/双轴投影收口 开始------
        // 只要策略已给出投影点，就统一将当前点与点击复用结果收口到该点；
        // 单轴命中时是单轴投影点，双轴命中时是交叉点。
        if (hvState.snapPoint) {
            currentPoint = hvState.snapPoint.clone();
            this._lastSnapResult = {
                strategyType: PointSnapStrategyType.PointHorizontalVertical,
                position: currentPoint,
                indicatorPosition: currentPoint,
                targetObject: null,
                weight: 0,
            };
        }
        //------ 与点水平/竖直单轴/双轴投影收口 结束------

        const fakeHvResult: PointSnapResult = {
            position: currentPoint,
            indicatorPosition: currentPoint,
            strategyType: PointSnapStrategyType.PointHorizontalVertical,
            targetObject: null,
            weight: 0,
        };

        if (hvState.hasHorizontal && hvState.hasVertical) {
            this.showSnapIndicator(fakeHvResult, '/textures/sketch/snap-horizontal.svg');
            this.viewer.pointerNearbyLabelManager.showPointSnapSecondaryIndicator('/textures/sketch/snap-vertical.svg');
        } else if (hvState.hasHorizontal) {
            this.showSnapIndicator(fakeHvResult, '/textures/sketch/snap-horizontal.svg');
            this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
        } else {
            this.showSnapIndicator(fakeHvResult, '/textures/sketch/snap-vertical.svg');
            this.viewer.pointerNearbyLabelManager.hidePointSnapSecondaryIndicator();
        }

        return currentPoint;
    }
    //------ 与点水平/竖直投影收口 结束------

    /**
     * 解析当前绘制流程临时允许参与点捕捉的点对象。
     * 说明：
     * 1. 当前仅对白名单开放“样条绘制中的首点预览对象”；
     * 2. 其它预览控制点继续保持不可捕捉，避免引入自吸附噪音；
     * 3. 返回值用于补充点捕捉请求，不写入正式草图组集合。
     * @returns 临时允许参与点捕捉的点对象列表。
     */
    private resolveTransientPointSnapObjects(): SketchPointObject[] {
        // 会话参考点由独立管理器托管，绘制层只读取其捕捉白名单。
        const transientPointObjects = [
            ...(this.viewer.sketchSessionReferencePointManager?.getPointObjects() ?? []),
        ];

        const activeTool = this.currentTool;
        if (activeTool === SketchDrawingTool.Spline) {
            const transientStartPoint = this.sketchSplineDrawingHandler.getTransientSnappableStartPoint();
            if (transientStartPoint) {
                transientPointObjects.push(transientStartPoint);
            }
            return transientPointObjects;
        }
        if (activeTool === SketchDrawingTool.StyleSpline) {
            const transientStartPoint = this.sketchStyleSplineDrawingHandler.getTransientSnappableStartPoint();
            if (transientStartPoint) {
                transientPointObjects.push(transientStartPoint);
            }
        }
        return transientPointObjects;
    }

    /**
     * 显示捕捉指示器，委托给 PointSnapManager 的指示器。
     * @param result 当前捕捉结果。
     * @param iconOverride 可选图标路径覆盖，优先级高于策略类型默认图标。
     */
    private showSnapIndicator(result: PointSnapResult, iconOverride?: string): void {
        this.viewer.pointerNearbyLabelManager.showPointSnapIndicator(result, iconOverride);
    }

    /**
     * 隐藏捕捉指示器，委托给 PointSnapManager 的指示器。
     */
    private hideSnapIndicator(): void {
        this.viewer.pointerNearbyLabelManager.hideLabel(PointerNearbyLabelPosition.RightBottom);
    }

    /**
     * 重置中点捕捉策略的可见状态。
     * 说明：绘制结束时调用，确保线段中点对象不残留在场景中。
     */
    private resetMidpointStrategy(): void {
        const snapManager = this.viewer.pointSnapManager;
        if (!snapManager) {
            return;
        }
        try {
            const strategy = snapManager.getStrategy(PointSnapStrategyType.Midpoint);
            if (strategy instanceof MidpointPointSnapStrategy) {
                strategy.reset();
            }
        } catch {
            // 中点策略未注册时忽略。
        }
    }

    /**
     * 重置象限点捕捉策略的可见状态。
     * 说明：绘制结束时调用，确保象限点对象不残留在场景中。
     */
    private resetQuadrantStrategy(): void {
        const snapManager = this.viewer.pointSnapManager;
        if (!snapManager) {
            return;
        }
        try {
            const strategy = snapManager.getStrategy(PointSnapStrategyType.Quadrant);
            if (strategy instanceof QuadrantPointSnapStrategy) {
                strategy.reset();
            }
        } catch {
            // 象限点策略未注册时忽略。
        }
    }

    // ------ 点捕捉集成 结束 ------

    //------ 草图参考面缺失错误日志 开始------
    /**
     * 根据鼠标事件解析当前草图参考面上的世界坐标。
     *
     * 设计说明：
     * 1. 草图绘制必须依赖当前参考面完成屏幕点到草图面的投影；
     * 2. 历史恢复或命令状态不同步时可能缺少参考面，此时不能抛出未捕获异常打断事件流；
     * 3. 这里输出可定位的错误日志并返回空值，让上层跳过本次绘制事件。
     *
     * @param event 鼠标事件。
     * @returns 草图参考面上的世界坐标；缺少参考面或射线未命中时返回 undefined。
     */
    getDrawingPoint(event: MouseEvent): THREE.Vector3 | undefined {
        const currentReferencePlane = this.viewer.currentReferencePlane;
        if (!currentReferencePlane){
            console.error('草图绘制点解析失败：当前草图参考面不存在。', {
                viewerMode: this.viewer.getMode?.(),
                viewMode: this.viewer.viewMode,
                activeSketchGroupId: this.viewer.getActiveSketchGroupId?.(),
                currentTool: this.currentTool,
                clientX: event.clientX,
                clientY: event.clientY,
            });
            return undefined;
        }
        const rectSource = this.viewer.renderer.domElement

        // 计算归一化设备坐标

        const rect = rectSource.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(x, y), this.viewer.camera);

        // 如果在基准面视图模式，投影到当前基准面

        const planeNormal = currentReferencePlane.normal;
        const planePoint = currentReferencePlane.coplanarPoint(new THREE.Vector3);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, planePoint);

        const intersection = new THREE.Vector3();
        const intersectedPoint = raycaster.ray.intersectPlane(plane, intersection)
        return intersectedPoint ?? undefined;
    }
    //------ 草图参考面缺失错误日志 结束------


    /**
     * 通知草图实体已创建。
     * 说明：所有处理器通过管理器统一派发实体创建事件，不再直接调用 renderer。
     * @param entity 新创建的草图实体。
     */
    public notifySketchEntityCreated(entity: THREE.Object3D): void {
        this.viewer.notifySketchEntityCreated(entity);
    }


    /**
     * 清除绘图预览
     */
    public clearDrawingPreview(): void {
        this.removePreviewLabel(this.previewRadiusLabel);
        this.previewRadiusLabel = null;
        this.viewer.pointerNearbyLabelManager.hideLabel(PointerNearbyLabelPosition.RightTop);

        if (this.previewLine && this.viewer.scene) {
            this.viewer.scene.remove(this.previewLine);
            scheduleDisposableResourcesDisposal([
                this.previewLine.geometry,
                this.previewLine.material as THREE.Material,
            ]);
            this.previewLine = null;
        }

        if (this.previewExtraLine && this.viewer.scene) {
            this.viewer.scene.remove(this.previewExtraLine);
            scheduleDisposableResourcesDisposal([
                this.previewExtraLine.geometry,
                this.previewExtraLine.material as THREE.Material,
            ]);
            this.previewExtraLine = null;
        }

        if (this.previewConstructionLines.length > 0 && this.viewer.scene) {
            this.previewConstructionLines.forEach((line) => {
                this.viewer.scene?.remove(line);
                scheduleDisposableResourceDisposal(line.geometry);
                if (line.material instanceof THREE.Material) {
                    scheduleDisposableResourceDisposal(line.material);
                }
            });
            this.previewConstructionLines = [];
        }

        if (this.previewCircle && this.viewer.scene) {
            this.viewer.scene.remove(this.previewCircle);
            scheduleDisposableResourcesDisposal([
                this.previewCircle.geometry,
                this.previewCircle.material as THREE.Material,
            ]);
            this.previewCircle = null;
        }

        if (this.previewRect && this.viewer.scene) {
            this.viewer.scene.remove(this.previewRect);
            scheduleDisposableResourcesDisposal([
                this.previewRect.geometry,
                this.previewRect.material as THREE.Material,
            ]);
            this.previewRect = null;
        }

        if (this.previewPoints && this.viewer.scene) {
            this.viewer.scene.remove(this.previewPoints);
            const previewPointMaterial = this.previewPoints.userData.previewPointMaterial;
            if (previewPointMaterial instanceof THREE.Material) {
                scheduleDisposableResourceDisposal(previewPointMaterial);
            }
            this.previewPoints.clear();
            this.previewPoints = null;
        }
        // 移除、隐藏预览与更新预览同样属于画面变化；多次请求由 Viewer 合并为一次提交。
        this.viewer.requestRender();
    }
    public removePreviewLabel(label: CSS2DObject | null): void {
        if (!label) return;
        this.viewer.scene?.remove(label);
        const element = label.element;
        if (element && element.parentElement) {
            element.parentElement.removeChild(element);
        }
    }

    //------ 角度捕捉标签 开始------

    /**
     * 显示角度捕捉标签。
     * 说明：
     * 1. 首次调用时惰性创建 CSS2DObject 标签，后续复用；
     * 2. 将标签定位到捕捉点（预览线末端）；
     * 3. 标签显示格式为 "45°"，定位在捕捉点右下角。
     * @param position 标签世界坐标（预览线末端 / 捕捉点）。
     * @param angleDeg 角度值（度），显示在标签中。
     */
    private showAngleSnapLabel(position: THREE.Vector3, angleDeg: number): void {
        this.viewer.pointerNearbyLabelManager.showTextLabel(
            position,
            PointerNearbyLabelPosition.RightTop,
            `${angleDeg}°`,
            {
                className: 'angle-snap-label',
            },
        );
    }

    /**
     * 显示预览右上角标签。
     * @param position 标签世界坐标。
     * @param labelText 标签文本。
     */
    public showPreviewAngleLabel(position: THREE.Vector3, labelText: string): void {
        this.viewer.pointerNearbyLabelManager.showTextLabel(
            position,
            PointerNearbyLabelPosition.RightTop,
            labelText,
            {
                className: 'angle-snap-label',
            },
        );
    }

    /**
     * 隐藏角度捕捉标签。
     * 说明：不销毁标签对象，仅设置为不可见，下次命中时复用。
     */
    private hideAngleSnapLabel(): void {
        this.viewer.pointerNearbyLabelManager.hideLabel(PointerNearbyLabelPosition.RightTop);
    }

    /**
     * 隐藏预览角度标签。
     */
    public hidePreviewAngleLabel(): void {
        this.hideAngleSnapLabel();
    }

    //------ 角度捕捉标签 结束------

}
